import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CloudDownload, Loader2, Trash2 } from "lucide-react";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const RedirectsManager = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ from_path: "", to_path: "" });
  const load = useCallback(() => axios.get(`${API}/admin/website/redirects`, { headers }).then((r) => setList(r.data)), [headers]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    try { await axios.post(`${API}/admin/website/redirects`, form, { headers }); setForm({ from_path: "", to_path: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't add redirect"); }
  };
  return (
    <div className="space-y-3" data-testid="redirects-manager">
      <p className="text-sm text-muted-foreground">Old web addresses forward to new ones, so old links and Google results keep working.</p>
      <div className="flex flex-wrap gap-2">
        <Input className="flex-1 min-w-[160px]" placeholder="/old-page" value={form.from_path} onChange={(e) => setForm({ ...form, from_path: e.target.value })} data-testid="redirect-from" />
        <Input className="flex-1 min-w-[160px]" placeholder="/new-page" value={form.to_path} onChange={(e) => setForm({ ...form, to_path: e.target.value })} data-testid="redirect-to" />
        <Button onClick={add} data-testid="redirect-add">Add redirect</Button>
      </div>
      <div className="rounded-xl border bg-white divide-y text-sm">
        {list.map((r) => (
          <div key={r.from_path} className="flex items-center gap-2 px-4 py-2" data-testid={`redirect-row-${r.from_path}`}>
            <code className="flex-1">{r.from_path}</code><span>→</span><code className="flex-1">{r.to_path}</code>
            <Button size="icon" variant="ghost" onClick={async () => { await axios.delete(`${API}/admin/website/redirects`, { headers, params: { from_path: r.from_path } }); load(); }}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const ImportPanel = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [job, setJob] = useState(null);
  const poll = useCallback(() => axios.get(`${API}/admin/website/import/status`, { headers }).then((r) => setJob(r.data)), [headers]);
  useEffect(() => { poll(); }, [poll]);
  useEffect(() => { if (job?.status !== "running") return undefined; const t = setInterval(poll, 3000); return () => clearInterval(t); }, [job?.status, poll]);
  const start = async () => {
    try { await axios.post(`${API}/admin/website/import`, {}, { headers }); poll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't start import"); }
  };
  return (
    <div className="space-y-3" data-testid="import-panel">
      <div className="rounded-xl border bg-white p-4 space-y-2 text-sm">
        <p className="font-semibold">Copy your current website (themathemind.com on B12)</p>
        <p className="text-slate-600">Copies all pages, text, images, videos and brochures into draft pages here, adds redirects for removed pages and fills the menu. Nothing goes live until you publish each page. Running it again refreshes the drafts.</p>
        <Button onClick={start} disabled={job?.status === "running"} data-testid="import-start">{job?.status === "running" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CloudDownload className="w-4 h-4 mr-1" />}{job?.status === "running" ? "Importing…" : "Import website"}</Button>
      </div>
      {job && job.status !== "none" && (
        <div className="rounded-xl border bg-slate-950 text-slate-200 p-3 text-xs font-mono max-h-72 overflow-auto" data-testid="import-log">
          <p className="mb-1 font-semibold" data-testid="import-status">Status: {job.status}{job.stats ? ` · ${job.stats.pages} pages, ${job.stats.media} media (${job.stats.media_failed} failed), ${job.stats.redirects} redirects` : ""}</p>
          {(job.log || []).map((l, i) => <p key={i}>{l}</p>)}
          {job.error && <p className="text-red-400">{job.error}</p>}
        </div>
      )}
    </div>
  );
};
